import React, { FC, useEffect, useRef, memo, forwardRef } from "react";
// libs
import mapboxgl, { Marker, Popup } from "mapbox-gl";
// component
import SearchAsMoveCheckbox from "./searchAsMoveCheckbox";
import MapClinicCard from "src/components/ui/Cards/MapClinicCard";
// types
import { ParsedUrlQuery } from "querystring";
// hooks
import Router from "next/router";
// theme
import { Wrapper } from "./map.styles";
// utils
import { renderToStaticMarkup } from "react-dom/server";
import { getBoundsByCenter } from "src/utils/map";
// types
import { IMapProps } from "./map.types";
import { LngLatBounds } from "src/constants/coordinates";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";

mapboxgl.accessToken = process.env.MAPBOX_PUBLIC_ACCESS_TOKEN as string;

let mapIsAutoFly = false;
const getMapIsAutoFly = () => mapIsAutoFly;
export const setMapIsAutoFly = (value: boolean) => (mapIsAutoFly = value);

const Map: FC<IMapProps> = ({ containerProps, heightImage }) => {
  const { ref, ne, sw, clinics, showSearchAsMoveCheckbox } = containerProps;

  const mapContainerRef = useRef(null);
  const mapRef: React.MutableRefObject<mapboxgl.Map | null> = useRef<mapboxgl.Map | null>(null);
  const mountedMarkersRef = useRef<Marker[]>([]);
  const mountedPopupsRef = useRef<Popup[]>([]);

  const getMap = (): mapboxgl.Map => mapRef.current as mapboxgl.Map;
  const setMap = (map: mapboxgl.Map): void => {
    mapRef.current = map;
    if (ref) ref.current = map;
  };

  const getPopups = () => mountedPopupsRef.current;
  const setPopups = (popup: Popup) =>
    (mountedPopupsRef.current = [...mountedPopupsRef.current, popup]);
  const clearPopups = () => (mountedPopupsRef.current = []);

  const markers = clinics
    .map((clinic) => ({ lng: clinic.data.lng, lat: clinic.data.lat, clinic }))
    .filter((marker) => !!marker.lat && !!marker.lng);

  const removeOldMarkers = () => {
    if (!!getMap() && !!mountedMarkersRef.current?.length) {
      mountedMarkersRef.current.forEach((marker) => marker.remove());
    }

    if (!!getPopups().length) clearPopups();
  };

  const setNewMarkers = () => {
    let map = getMap();

    if (!!map && !!markers?.length) {
      mountedMarkersRef.current = markers.map((marker) => {
        const el = document.createElement("div");
        el.className = "marker";

        const popup = new mapboxgl.Popup({
          offset: 25,
          closeButton: false,
          closeOnClick: false,
          anchor: "top",
          closeOnMove: false,
          className: "clinic-popup",
        }).setHTML(
          renderToStaticMarkup(
            <div style={{ transform: "scale(0.9)" }}>
              <MapClinicCard
                name={marker.clinic.name}
                address={marker.clinic.address}
                rating={marker.clinic.rating}
                logo_url={marker.clinic.logo_url}
                data={marker.clinic.data}
                heightImage={heightImage}
              />
            </div>,
          ),
        );

        // save into ref
        setPopups(popup);

        const createdMarker = new Marker(el)
          .setLngLat([marker.lng, marker.lat])
          .setPopup(popup)
          .addTo(map);

        createdMarker.getElement().addEventListener("click", (e) => {
          // prevent double toggle popup
          e.stopPropagation();
          amplitudeEvent(formatEventName("search_results", "map", "pin"));

          // clear all selected markers blue icons
          mountedMarkersRef.current.forEach((marker) =>
            marker.getElement().classList.remove("marker-active"),
          );
          // set blue icon to the maker
          createdMarker.getElement().classList.add("marker-active");

          // close all popups
          const createdPopups = getPopups();
          if (!!createdPopups) {
            createdPopups.forEach((popup) => popup.isOpen() && popup.remove());
          }
          // open popup
          createdMarker.togglePopup();
          // move marker to the center of the map
          // disable feature "Search as I move" and enable after fly to marker
          setMapIsAutoFly(true);
          // Start:
          const center = createdMarker.getLngLat();
          const zoom = map.getZoom();
          const boundsByCenter = getBoundsByCenter(center, zoom);

          // @ts-ignore
          const dif = boundsByCenter.ne.lat - center.lat;
          const moveToLat = center.lat - dif;

          let duration = 500;
          map.flyTo({
            center: [createdMarker.getLngLat().lng, moveToLat],
            duration,
          });
          // Finish
          // enable feature "Search as I move"
          setTimeout(() => {
            setMapIsAutoFly(false);
          }, duration + 100);

          if (popup.isOpen()) {
            const close = document.getElementById("close-clinic-popup-button");

            if (close) {
              close.addEventListener("click", (e) => {
                // prevent redirect to clinic
                e.stopPropagation();
                // close popup
                createdMarker.getPopup().remove();
                // change marker icon to white
                createdMarker.getElement().classList.remove("marker-active");
              });
            }

            createdMarker
              .getPopup()
              .getElement()
              .addEventListener("click", (e) => {
                e.stopPropagation();
                amplitudeEvent(formatEventName("search_results", "map", "clinic"));
                Router.router?.push(
                  marker.clinic.slug.startsWith("/")
                    ? `/clinics${marker.clinic.slug}`
                    : `/clinics/${marker.clinic.slug}`,
                );
              });
          }
        });

        return createdMarker;
      });
    }
  };

  // Initialize map when component mounts
  useEffect(() => {
    const map = new mapboxgl.Map({
      // @ts-ignore
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      bounds: [ne, sw],
    });

    // Add navigation control (the +/- zoom buttons)
    map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
      }),
      "top-right",
    );

    setMap(map);
    setNewMarkers();

    // Clean up on unmount
    return () => map.remove();
  }, []);

  useEffect(() => {
    removeOldMarkers();
    setNewMarkers();
  }, [markers]);

  const onMoveEnd = async (oldQuery: ParsedUrlQuery | undefined) => {
    if (getMapIsAutoFly()) return;

    const [asPathname] = Router.router?.asPath.split("?") as string[];
    const map = getMap();

    const center = map.getCenter();
    const mapBounds = map.getBounds();
    const bounds: LngLatBounds = {
      ne: {
        lng: +mapBounds.getNorthEast().lng.toFixed(4),
        lat: +mapBounds.getNorthEast().lat.toFixed(4),
      },
      sw: {
        lng: +mapBounds.getSouthWest().lng,
        lat: +mapBounds.getSouthWest().lat,
      },
    };

    const query: any = {
      ...Router.router?.query,
      _start: 0,
      geo_order: `${center.lat},${center.lng}`,
      ne: [bounds.ne.lng, bounds.ne.lat],
      sw: [bounds.sw.lng, bounds.sw.lat],
    };

    if (oldQuery?.area?.includes("service")) {
      query.services = [query.area?.[1]];
    }

    // @ts-ignore
    delete query.area;

    await Router.replace(
      {
        pathname: "/veterinarian/[...area]",
        query,
      },
      {
        pathname: "/veterinarian/search",
        query,
      },
      { shallow: true, scroll: false },
    );
  };

  const onChangeCheckbox = async (value: boolean, oldQuery: ParsedUrlQuery | undefined) => {
    const map = getMap();

    if (!!map && value) {
      // add listener
      map.on("moveend", onMoveEnd);
      // update URL, add coordinates
      onMoveEnd(oldQuery);
    }

    if (!!map && !value) {
      // remove listener
      map.off("moveend", onMoveEnd);
    }
  };

  return (
    <Wrapper>
      {showSearchAsMoveCheckbox && <SearchAsMoveCheckbox callback={onChangeCheckbox} />}
      <MemoizedContainer ref={mapContainerRef} />
    </Wrapper>
  );
};

const Container = forwardRef((props: any, ref: any) => (
  <div className="map-container" {...props} ref={ref} />
));
const MemoizedContainer = memo(Container, () => true);

export default memo(Map);
